import { useEffect, forwardRef, useImperativeHandle } from 'react'
import { faPlus } from '@fortawesome/free-solid-svg-icons'
import { FormTable } from '../FormTable/index.tsx'
import { useCompanyTeamController } from './utils/controller.ts'
import { getColumns } from './utils/fieldConfigs.tsx'
import { SECTION_COMPANY_TEAM } from '../../../state_manager/constants/strings.ts'
import type { TeamMember } from './utils/utils.ts'

interface CompanyTeamTableProps {
  mode?: 'new' | 'existing'
  companyId?: string | null
  initialTeamMembers?: TeamMember[]
  onChange?: ((members: TeamMember[], getTeamMembersForAPI?: (recordId: string) => any[]) => void) | null
  getCurrentTeamMembers?: ((getTeamMembersForAPI: (recordId: string) => any[]) => void) | null
  readOnly?: boolean
  title?: string
  hideFounderColumn?: boolean
  enforcedFounderStatus?: boolean | null
  hideCategoryColumn?: boolean
  hideJobTitleColumn?: boolean
  nameColumnEditable?: boolean
  showDeleteOnly?: boolean
  hideRequiredMarks?: boolean
  isExEmployees?: boolean
}

export interface CompanyTeamTableRef {
  hasPendingTeamMembers: () => boolean
}

export const CompanyTeamTable = forwardRef<CompanyTeamTableRef, CompanyTeamTableProps>(
  (
    {
      mode = 'new',
      companyId = null,
      initialTeamMembers = [],
      onChange = null,
      getCurrentTeamMembers = null,
      readOnly = false,
      title = SECTION_COMPANY_TEAM,
      hideFounderColumn = false,
      enforcedFounderStatus = null,
      hideCategoryColumn = false,
      hideJobTitleColumn = false,
      showDeleteOnly = false,
      hideRequiredMarks = false,
      isExEmployees = false
    },
    ref
  ) => {
    const {
      teamMembers,
      editingRowKey,
      availableCategoryOptions,
      isAddingNewMember,
      savingRowKey,
      deletingRowKey,
      validationErrors,
      emailWarnings,
      firstErrorFieldKey,
      fieldResetKeys,
      savedEntries,
      handleAddMember,
      handleRemoveMember,
      handleResignMember,
      handleFieldChange,
      handleAddContactFromEmail,
      startEditing,
      cancelEditing,
      saveRow,
      getTeamMembersForAPI,
      hasPendingTeamMembers
    } = useCompanyTeamController({
      mode,
      companyId,
      initialTeamMembers,
      onChange,
      enforcedFounderStatus,
      hideCategoryColumn,
      isExEmployees
    })

    useImperativeHandle(
      ref,
      () => ({
        hasPendingTeamMembers
      }),
      [hasPendingTeamMembers]
    )

    useEffect(() => {
      if (mode === 'new' && getCurrentTeamMembers) {
        getCurrentTeamMembers(getTeamMembersForAPI)
      }
    }, [mode, getCurrentTeamMembers, getTeamMembersForAPI])

    useEffect(() => {
      if (firstErrorFieldKey && editingRowKey === firstErrorFieldKey.rowKey && editingRowKey) {
        const timeoutId = setTimeout(() => {
          if (!editingRowKey) return

          const fieldKey = firstErrorFieldKey.fieldKey

          const tableRow = document.querySelector(`tr[data-row-key="${firstErrorFieldKey.rowKey}"]`)
          if (tableRow) {
            const cells = Array.from(tableRow.querySelectorAll('td'))

            const fieldToColumnMap = {
              contact: 'contact',
              founder: 'founder',
              category: 'category',
              jobTitle: 'jobTitle',
              email: 'email',
              contactNumber: 'contactNumber'
            }

            let targetCell: HTMLTableCellElement | null = null
            for (const cell of cells) {
              const hasInput = cell.querySelector('input, textarea, .ant-select-selector, .ant-select')
              if (hasInput) {
                const columnIndex = Object.keys(fieldToColumnMap).indexOf(fieldKey)
                if (columnIndex !== -1) {
                  const dataIndexCells = cells.filter((cell) => {
                    return (
                      cell.querySelector('input, textarea, .ant-select-selector, .ant-select') &&
                      !cell.querySelector('button[class*="ant-btn"]')
                    )
                  })

                  if (dataIndexCells[columnIndex]) {
                    targetCell = dataIndexCells[columnIndex]
                    break
                  }
                }
              }
            }

            if (targetCell) {
              const input = targetCell.querySelector<HTMLElement>('input, textarea, .ant-select-selector, .ant-select')
              if (input) {
                if (input.classList.contains('ant-select')) {
                  const selector = input.querySelector<HTMLElement>('.ant-select-selector')
                  if (selector) {
                    selector.focus({ preventScroll: true })
                    setTimeout(() => selector.click(), 50)
                  }
                } else {
                  input.focus({ preventScroll: true })
                }
              }
            }
          }
        }, 200)

        return () => clearTimeout(timeoutId)
      }
      return undefined
    }, [firstErrorFieldKey, editingRowKey])

    const columns = getColumns({
      editingRowKey,
      savedEntries,
      availableCategoryOptions,
      firstErrorFieldKey,
      fieldResetKeys,
      validationErrors,
      emailWarnings,
      handleFieldChange,
      handleAddContactFromEmail,
      startEditing,
      readOnly,
      hideFounderColumn,
      hideCategoryColumn,
      hideJobTitleColumn,
      hideRequiredMarks
    })

    const isReadOnly = Boolean(readOnly)
    const onAddHandler = isReadOnly ? undefined : handleAddMember || undefined

    return (
      <FormTable
        title={title}
        dataSource={teamMembers}
        columns={columns}
        addButtonText=''
        addButtonIcon={faPlus}
        addButtonTestId='add-team-member-button'
        emptyText='No team members found'
        isAddingNew={isReadOnly ? false : isAddingNewMember}
        onAdd={onAddHandler}
        className='compact-team-table'
        includeActions={isReadOnly ? false : true}
        actionsWidth={60}
        mode={mode}
        editingRowKey={editingRowKey}
        savingRowKey={savingRowKey}
        deletingRowKey={deletingRowKey}
        onRemove={handleRemoveMember}
        onResign={showDeleteOnly ? null : handleResignMember}
        onSave={saveRow}
        onCancel={cancelEditing}
        validateRecord={null}
        deleteConfirmTitle='Delete Team Member'
        deleteConfirmDescription='Are you sure you want to remove this team member from the Company Team Table?'
        resignConfirmTitle='Terminate Team Member'
        resignConfirmDescription='Are you sure you want to terminate this team member?'
        useDeleteConfirm={mode === 'existing'}
      />
    )
  }
)

CompanyTeamTable.displayName = 'CompanyTeamTable'
